import { Controller } from "@hotwired/stimulus"

// Coda di caricamento con avanzamento per file. Sorgenti: bottone Carica,
// rilascio sulla pagina, rilascio su una cartella (via cloud-drag), incolla.
// Ogni file viaggia in una richiesta propria; nella cartella corrente la
// risposta turbo-stream appende la tessera senza ricaricare la pagina.
export default class extends Controller {
  static targets = ["overlay", "overlayText", "tray", "trayTitle", "trayList", "rowTemplate", "i18n"]
  static values = { urlTemplate: String, folderId: String, folderName: String }

  connect() {
    this._depth = 0
    this._queue = []
    this._active = 0
    this._concurrency = 2
    this._pending = 0
  }

  // ── Sorgenti ──────────────────────────────────────────────────────────────

  pick(event) {
    const files = Array.from(event.target.files || [])
    event.target.value = ""
    this.uploadHere(files)
  }

  paste(event) {
    if (event.target.matches("input, textarea, [contenteditable]")) return
    const files = Array.from(event.clipboardData?.files || [])
    if (!files.length) return
    event.preventDefault()
    this.uploadHere(files)
  }

  enter(event) {
    if (!this.hasFiles(event)) return
    event.preventDefault()
    this._depth++
    this.showOverlay()
  }

  over(event) {
    if (!this.hasFiles(event)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = this.folderIdValue ? "copy" : "none"
  }

  leave(event) {
    if (!this.hasFiles(event)) return
    this._depth = Math.max(0, this._depth - 1)
    if (this._depth === 0) this.hideOverlay()
  }

  drop(event) {
    if (!this.hasFiles(event)) return
    event.preventDefault()
    this._depth = 0
    this.hideOverlay()
    this.uploadHere(Array.from(event.dataTransfer.files))
  }

  hasFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes("Files")
  }

  showOverlay() {
    if (!this.hasOverlayTarget) return
    const t = this.i18n
    this.overlayTextTarget.textContent = this.folderIdValue ? t.dropIn.replace("%{name}", this.folderNameValue) : t.noFolder
    this.overlayTarget.classList.toggle("is-blocked", !this.folderIdValue)
    this.overlayTarget.hidden = false
  }

  hideOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.hidden = true
  }

  // ── Coda ──────────────────────────────────────────────────────────────────

  uploadHere(files) {
    if (!files.length) return
    if (!this.folderIdValue) { this.note(this.i18n.noFolder, "error"); return }
    this.uploadTo(this.folderIdValue, files, this.folderNameValue)
  }

  uploadTo(folderId, files, folderName) {
    if (!files.length) return
    this.openTray()
    files.forEach((file) => {
      const row = this.addRow(file, folderName, folderId)
      this._queue.push({ file, folderId: String(folderId), folderName, row })
    })
    this.pump()
  }

  pump() {
    while (this._active < this._concurrency && this._queue.length) {
      const job = this._queue.shift()
      this._active++
      this._pending++
      this.send(job).finally(() => { this._active--; this._pending--; this.pump(); this.maybeAutoClose() })
    }
  }

  send(job) {
    return new Promise((resolve) => {
      const here = job.folderId === String(this.folderIdValue)
      const xhr = new XMLHttpRequest()
      const url = this.urlTemplateValue.replace(":id", job.folderId)
      xhr.open("POST", url)
      xhr.setRequestHeader("X-CSRF-Token", document.querySelector("meta[name='csrf-token']")?.content || "")
      xhr.setRequestHeader("Accept", here ? "text/vnd.turbo-stream.html" : "text/html")
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) this.setRow(job.row, "uploading", Math.round((e.loaded / e.total) * 100))
      })
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (here && (xhr.getResponseHeader("Content-Type") || "").includes("turbo-stream")) {
            window.Turbo.renderStreamMessage(xhr.responseText)
            this.element.dispatchEvent(new CustomEvent("cloud:changed", { bubbles: true }))
          }
          this.setRow(job.row, "done", 100, here ? null : this.i18n.uploadedElsewhere.replace("%{name}", job.folderName || ""))
        } else {
          this.fail(job)
        }
        resolve()
      })
      xhr.addEventListener("error", () => { this.fail(job); resolve() })
      const form = new FormData()
      form.append("files[]", job.file)
      xhr.send(form)
      this.setRow(job.row, "uploading", 0)
    })
  }

  fail(job) {
    this.setRow(job.row, "failed", 0)
    const retry = job.row.querySelector(".cloud-tray__retry")
    retry.hidden = false
    retry.onclick = () => {
      retry.hidden = true
      this._queue.push(job)
      this.pump()
    }
  }

  // ── Vassoio ───────────────────────────────────────────────────────────────

  openTray() {
    if (!this.hasTrayTarget) return
    clearTimeout(this._closeTimer)
    this.trayTarget.hidden = false
  }

  closeTray() {
    if (this.hasTrayTarget) this.trayTarget.hidden = true
    if (this.hasTrayListTarget) this.trayListTarget.innerHTML = ""
  }

  maybeAutoClose() {
    if (this._pending > 0 || this._queue.length) return
    const failed = this.trayListTarget.querySelector("[data-state='failed']")
    if (failed) return
    clearTimeout(this._closeTimer)
    this._closeTimer = setTimeout(() => this.closeTray(), 4000)
  }

  addRow(file, folderName, folderId) {
    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    row.querySelector(".cloud-tray__name").textContent = file.name
    row.querySelector(".cloud-tray__name").title = file.name
    this.trayListTarget.prepend(row)
    this.setRow(row, "queued", 0)
    return row
  }

  setRow(row, state, percent, note) {
    const t = this.i18n
    row.dataset.state = state
    row.querySelector(".cloud-tray__bar > i").style.width = `${percent}%`
    const status = row.querySelector(".cloud-tray__status")
    status.textContent = note || { queued: t.queued, uploading: t.uploading.replace("%{percent}", percent), done: t.done, failed: t.failed }[state]
  }

  note(message, kind = "success") {
    const el = document.createElement("div")
    el.className = `cloud-toast cloud-toast--${kind}`
    el.textContent = message
    document.body.appendChild(el)
    requestAnimationFrame(() => el.classList.add("is-in"))
    setTimeout(() => { el.classList.remove("is-in"); setTimeout(() => el.remove(), 250) }, 4000)
  }

  get i18n() {
    const d = this.hasI18nTarget ? this.i18nTarget.dataset : {}
    return { queued: d.queued, done: d.done, failed: d.failed, uploading: d.uploading, dropIn: d.dropIn, uploadedElsewhere: d.uploadedElsewhere, noFolder: d.noFolder }
  }
}
